import { ListenableSubscribable, Subscription } from '@benzinga/subscribable';
import { arrayDeepEqual } from '@benzinga/utils';
import { UserSettingsManager } from '@benzinga/user-settings';
import { Session } from '@benzinga/session';
import { ScannerConfig, ScannerSettingsFeed } from './entities';
import { ScannerUserSettingConfig } from './ingress';

interface ScannerEvent {
  type: 'scanner:created_scanner' | 'scanner:updated_scanner' | 'scanner:removed_scanner';
  scanner: ScannerConfig;
}

interface ScannerUpdateEvent {
  type: 'scanner:updated_scanners';
  scanners: ScannerConfig[];
}

export type ScannerConfigManagerEvent = ScannerUpdateEvent | ScannerEvent;

export class ScannerConfigManager extends ListenableSubscribable<ScannerConfigManagerEvent> {
  private settingsFeed: ScannerSettingsFeed;
  private feedSubscription?: Subscription<ScannerSettingsFeed>;

  constructor(session: Session) {
    super();
    this.settingsFeed = session.getManager(UserSettingsManager).getFeed(ScannerUserSettingConfig);
    this.fetchConfigs();
  }

  public static getName = () => 'benzinga-scanner-config';

  public fetchConfigs = async (force?: boolean): Promise<void> => {
    await this.settingsFeed.fetchValue(force);
  };

  public getConfigs = (): ScannerConfig[] => {
    return this.settingsFeed.getValue().configs;
  };
  // returns true if update was committed
  public updateConfigs = (scanners: ScannerConfig[]): boolean => {
    if (!arrayDeepEqual(this.getConfigs(), scanners)) {
      this.settingsFeed.setValue(settings => ({ ...settings, configs: scanners }));
      return true;
    }
    return false;
  };

  public addConfig = (scanner: ScannerConfig) => {
    const res = this.updateConfigs([...this.getConfigs(), scanner]);
    if (res) {
      this.dispatch({
        scanner: scanner,
        type: 'scanner:created_scanner',
      });
    }
  };

  public updateConfig = (scanner: ScannerConfig) => {
    this.settingsFeed.setValue(scans => ({
      ...scans,
      configs: scans.configs.map(item => (item.uuid === scanner.uuid ? scanner : item)),
    }));
    this.dispatch({
      scanner,
      type: 'scanner:updated_scanner',
    });
  };

  public removeConfig = (scanner: ScannerConfig) => {
    this.settingsFeed.setValue(scans => ({
      ...scans,
      configs: scans.configs.filter(s => s.uuid !== scanner.uuid),
    }));
    this.dispatch({
      scanner,
      type: 'scanner:removed_scanner',
    });
  };

  public clearConfigs = () => {
    this.settingsFeed.setValue(scans => ({
      ...scans,
      configs: [],
    }));
  };

  override onFirstSubscription = (): void => {
    this.feedSubscription = this.settingsFeed.subscribe(event => {
      if (event.type === 'settings_feed:update') {
        this.dispatch({
          scanners: event.value.configs,
          type: 'scanner:updated_scanners',
        });
      }
    });
  };

  protected override onZeroSubscriptions(): void {
    this.feedSubscription?.unsubscribe();
    this.feedSubscription = undefined;
  }
}
